import { useState } from "react";
import { motion } from "framer-motion";
import { Camera, Network, Eye, Wrench, Check, ArrowRight, Phone } from "lucide-react";
import { SERVICES, CALL_SALES, BRAND } from "../lib/constants";
import QuoteDialog from "../components/QuoteDialog";
import Seo from "../components/Seo";

const icons = { cctv: Camera, networking: Network, surveillance: Eye, amc: Wrench };

export default function Services() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteService, setQuoteService] = useState(null);

  const openQuote = (service) => {
    setQuoteService(service || null);
    setQuoteOpen(true);
  };

  return (
    <div data-testid="services-page" className="bg-white">
      <Seo
        title="CCTV, Surveillance, Networking & AMC Services in Nagpur"
        description="Hayat Security offers CCTV installation, commercial surveillance, structured networking and AMC/repair in Nagpur — covering Sitabuldi, Dharampeth, Civil Lines, Hingna, MIDC Butibori. Free site visit. Call +91 96738 78652."
        canonicalPath="/services"
      />
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="text-sm font-semibold text-[#1C6B8A] uppercase tracking-wider">Services</div>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-slate-900 tracking-tight mt-3">
              CCTV, networking & surveillance — done right
            </h1>
            <p className="text-slate-600 mt-5 text-lg">
              Four core services, handled by our in-house team. Transparent pricing, clean installations, reliable after-service.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {SERVICES.map((s, idx) => {
            const Icon = icons[s.id];
            const reverse = idx % 2 === 1;
            return (
              <motion.div
                key={s.id}
                id={s.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                data-testid={`service-detail-${s.id}`}
                className={`grid lg:grid-cols-2 gap-10 items-center ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}
              >
                <div className="relative">
                  <div className="rounded-3xl overflow-hidden border border-slate-200">
                    <img src={s.image} alt={s.title} className="w-full h-[400px] object-cover" />
                  </div>
                  <div className="absolute top-5 left-5 bg-white rounded-full px-4 py-2 shadow-md flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#1C6B8A] text-white flex items-center justify-center">
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-xs font-semibold text-slate-700">Service {idx + 1}</span>
                  </div>
                </div>

                <div>
                  <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900">{s.title}</h2>
                  <p className="text-slate-600 mt-4 leading-relaxed text-lg">{s.description}</p>
                  <ul className="mt-6 space-y-3">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-[#E8F4F8] text-[#1C6B8A] flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="w-3 h-3" />
                        </div>
                        <span className="text-slate-700">{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <button
                      onClick={() => openQuote(s.title)}
                      data-testid={`detail-quote-${s.id}`}
                      className="inline-flex items-center gap-2 h-12 px-6 rounded-full brand-btn text-sm font-semibold"
                    >
                      Get Free Quote <ArrowRight className="w-4 h-4" />
                    </button>
                    <a
                      href={CALL_SALES}
                      data-testid={`detail-call-${s.id}`}
                      className="inline-flex items-center gap-2 h-12 px-6 rounded-full brand-outline-btn text-sm font-semibold"
                    >
                      <Phone className="w-4 h-4" /> Call Sales
                    </a>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900">
            Not sure which service fits your needs?
          </h2>
          <p className="text-slate-600 mt-4">
            Share your requirement and we'll guide you — no obligation, no pressure.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              onClick={() => openQuote()}
              data-testid="services-cta-quote"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full brand-btn text-sm font-semibold"
            >
              Request Site Visit <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href={CALL_SALES}
              data-testid="services-cta-call"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full brand-outline-btn text-sm font-semibold"
            >
              <Phone className="w-4 h-4" /> {BRAND.salesDisplay}
            </a>
          </div>
        </div>
      </section>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} defaultService={quoteService} />
    </div>
  );
}
